import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/cn';

export interface PaginationProps {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onLimitChange?: (limit: number) => void;
  className?: string;
}

const PAGE_SIZES = [10, 25, 50, 100];

export function Pagination({
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  onLimitChange,
  className,
}: PaginationProps) {
  const firstRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastRow = Math.min(page * limit, total);

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2',
        className,
      )}
    >
      <p className="text-xs text-content-muted">
        {total === 0 ? (
          'No results'
        ) : (
          <>
            Showing <span className="font-medium text-content">{firstRow}</span>–
            <span className="font-medium text-content">{lastRow}</span> of{' '}
            <span className="font-medium text-content">{total}</span>
          </>
        )}
      </p>

      <div className="flex items-center gap-3">
        {onLimitChange && (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-content-muted">Rows</span>
            <Select
              value={String(limit)}
              onChange={(next) => {
                onLimitChange(Number(next));
              }}
              options={PAGE_SIZES.map((size) => ({ value: String(size), label: String(size) }))}
              className="h-7 w-18"
            />
          </div>
        )}

        <div className="flex items-center gap-1">
          <Button
            variant="secondary"
            size="sm"
            iconOnly
            iconLeft="chevron-left"
            aria-label="Previous page"
            disabled={page <= 1}
            onClick={() => {
              onPageChange(page - 1);
            }}
          />

          <span className="px-1.5 text-xs whitespace-nowrap text-content-muted">
            Page {page} of {Math.max(totalPages, 1)}
          </span>

          <Button
            variant="secondary"
            size="sm"
            iconOnly
            iconLeft="chevron-right"
            aria-label="Next page"
            disabled={page >= totalPages}
            onClick={() => {
              onPageChange(page + 1);
            }}
          />
        </div>
      </div>
    </div>
  );
}
